import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  Tooltip,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CategoryIcon from "@mui/icons-material/Category";
import PaletteIcon from "@mui/icons-material/Palette";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import FlagIcon from "@mui/icons-material/Flag";
import * as MuiIcons from "@mui/icons-material";
import api, { API_BASE_URL } from "../../../../utils/api";
import RequestFilesViewer from "./RequestFilesViewer";

const RequestDetailsPanel = ({ requestDetails }) => {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [users, setUsers] = useState([]);
  const [color, setColor] = useState(null);
  const [letterFiles, setLetterFiles] = useState([]);
  const [letterPreview, setLetterPreview] = useState({ open: false, src: "", blobUrl: null, loading: false });

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const softCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    backgroundColor: "#fff",
  };

  const sectionIconWrapSx = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const SectionTitle = ({ icon, title, subtitle }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mb: 1.5,
        minWidth: 0,
      }}
    >
      <Box sx={sectionIconWrapSx}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.3 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const DetailCard = ({ icon, label, value, extra = null }) => (
    <Card sx={{ ...softCardSx, p: 2, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box sx={sectionIconWrapSx}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
          >
            {label}
          </Typography>

          {extra ? (
            extra
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: "#223142",
                fontWeight: 600,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {value || "N/A"}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!requestDetails) return;

      try {
        const [typeRes, subtypeRes, priorityRes, userRes] = await Promise.all([
          api.get(`/architectural-request-types`),
          api.get(
            `/architectural-request-subtypes?typeId=${
              requestDetails?.request_type?.[0] || ""
            }`
          ),
          api.get(`/priority-levels`),
          api.get(`/users-by-role`),
        ]);

        setTypes(typeRes.data || []);
        setSubtypes(subtypeRes.data || []);
        setPriorities(priorityRes.data || []);
        setUsers(userRes.data || []);

        if (requestDetails?.color_id) {
          const colorRes = await api.get(
            `/architectural-request-colors/${requestDetails.color_id}`
          );
          const colorData = Array.isArray(colorRes.data)
            ? colorRes.data[0]
            : colorRes.data;
          setColor(colorData || null);
        } else {
          setColor(null);
        }
      } catch (err) {
        console.error("Failed to load request details data:", err);
      }
    };

    fetchData();
  }, [requestDetails]);

  useEffect(() => {
    if (!requestDetails?.id) return;
    api.get(`/architectural-requests/${requestDetails.id}/letter-files`)
      .then(res => setLetterFiles(res.data || []))
      .catch(() => setLetterFiles([]));
  }, [requestDetails?.id]);

  const requestTypeName = requestDetails?.request_type?.[1] || "N/A";
  const subtypeName =
    subtypes.find((s) => s.subtype_id === Number(requestDetails?.subtype_id))
      ?.subtype_name || "N/A";

  const priorityName =
    priorities.find((p) => p.priority_value === requestDetails?.priority)
      ?.display_name || "N/A";

  const assignedTo = users.find((u) => u.UserID === requestDetails?.assigned_to);
  const assignedToName = assignedTo
    ? `${assignedTo.Name} ${assignedTo.Lastname} (${assignedTo.RoleName})`
    : "N/A";

  const statusNormalized = String(requestDetails?.status || "").toLowerCase();
  const statusChipSx =
    statusNormalized === "approved"
      ? {
          borderRadius: "999px",
          fontWeight: 700,
          backgroundColor: "rgba(34,197,94,0.12)",
          color: "#15803d",
        }
      : {
          borderRadius: "999px",
          fontWeight: 700,
          backgroundColor: "rgba(239,68,68,0.12)",
          color: "#b91c1c",
        };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ ...pageCardSx, p: 2.5 }}>
        <SectionTitle
          icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
          title="Request Details"
          subtitle={`Request ID: REQ-${requestDetails?.id}`}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DetailCard
              icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Request Type"
              value={requestTypeName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailCard
              icon={<CategoryIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Subtype"
              value={subtypeName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailCard
              icon={<FlagIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Priority"
              value={priorityName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailCard
              icon={<AssignmentIndIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Assigned To"
              value={assignedToName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailCard
              icon={<PaletteIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Color"
              extra={
                color ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title={color.color}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          backgroundColor: color.color,
                          border: "1px solid #ccc",
                          boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                    <Typography
                      variant="body2"
                      sx={{ color: "#223142", fontWeight: 600 }}
                    >
                      {color.color?.startsWith("#")
                        ? color.color.toUpperCase()
                        : color.color}
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "#223142", fontWeight: 600 }}
                  >
                    None selected
                  </Typography>
                )
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailCard
              icon={<CheckCircleIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Status"
              extra={<Chip size="small" label={requestDetails?.status || "N/A"} sx={statusChipSx} />}
            />
          </Grid>

          <Grid item xs={12}>
            <DetailCard
              icon={<DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              label="Description"
              value={requestDetails?.description || "No Description Available"}
            />
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ ...pageCardSx, p: 2.5 }}>
        <SectionTitle
          icon={<DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
          title="Request Files"
          subtitle="Uploaded files and supporting documents for this request."
        />

        <RequestFilesViewer requestId={requestDetails?.id} />
      </Card>

      {letterFiles.length > 0 && (
        <Card sx={{ ...pageCardSx, p: 2.5 }}>
          <SectionTitle
            icon={<MuiIcons.Gavel sx={{ color: "#2f63ad", fontSize: 18 }} />}
            title="Letter Documents"
            subtitle="Official letters generated for this architectural request."
          />
          <Grid container spacing={2}>
            {letterFiles.map((file) => {
              const fileUrl = `${API_BASE_URL}${file.file_url}`;
              return (
                <Grid item xs={12} sm={6} md={4} key={file.id}>
                  <Card sx={{ ...softCardSx, p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, textAlign: "center", height: "100%" }}>
                    <MuiIcons.PictureAsPdf color="error" sx={{ fontSize: 48 }} />
                    <Typography variant="body2" sx={{ wordBreak: "break-word", fontWeight: 600, color: "#223142" }}>
                      {file.file_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#8a94a6" }}>
                      {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : ""}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                      <Button size="small" variant="outlined"
                        startIcon={<MuiIcons.Visibility />}
                        onClick={async () => {
                          setLetterPreview({ open: true, src: fileUrl, blobUrl: null, loading: true });
                          try {
                            const res = await fetch(fileUrl, { credentials: "include" });
                            const blob = await res.blob();
                            setLetterPreview(p => ({ ...p, blobUrl: URL.createObjectURL(blob), loading: false }));
                          } catch {
                            setLetterPreview(p => ({ ...p, loading: false }));
                          }
                        }}
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: 11, borderColor: "#2f63ad", color: "#2f63ad" }}>
                        Preview
                      </Button>
                      <Button size="small" variant="contained" href={fileUrl} target="_blank" rel="noopener noreferrer" download
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: 11, backgroundColor: "#2f63ad", boxShadow: "none", "&:hover": { backgroundColor: "#1f4f94" } }}>
                        Download
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Letter preview dialog */}
          <Dialog open={letterPreview.open} onClose={() => { if (letterPreview.blobUrl) URL.revokeObjectURL(letterPreview.blobUrl); setLetterPreview({ open: false, src: "", blobUrl: null, loading: false }); }}
            maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,0.22)", height: "92vh" } }}>
            <DialogContent sx={{ position: "relative", p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
              <IconButton onClick={() => { if (letterPreview.blobUrl) URL.revokeObjectURL(letterPreview.blobUrl); setLetterPreview({ open: false, src: "", blobUrl: null, loading: false }); }}
                sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, bgcolor: "rgba(0,0,0,0.12)", "&:hover": { bgcolor: "rgba(0,0,0,0.25)" } }}>
                <MuiIcons.Close sx={{ color: "#223142" }} />
              </IconButton>
              {letterPreview.loading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <iframe src={letterPreview.blobUrl || letterPreview.src} title="Letter Preview" width="100%"
                  style={{ flex: 1, border: "none", borderRadius: "18px", minHeight: "80vh" }} />
              )}
            </DialogContent>
          </Dialog>
        </Card>
      )}
    </Box>
  );
};

export default RequestDetailsPanel;